/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAccess } from '@/lib/access';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, isApiError } from '../../api';
import type { DossierDetail } from '../../api/areas/accounts';
import { Callout, DataTable, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { formatDate, formatDateTime } from '../../lib/format';
import { useLabels } from '../../lib/labels';
import { useApi } from '../../lib/useApi';
import { SystemReason } from '../../components/SystemReason';
import { humanize, LIFECYCLE, StatusBadge } from '../domains/status';
import { cellLink, CLASSIFICATION, DOC_VERIFICATION, DOSSIER_STATUS, EnumBadge, EnumText, formatBytes, MissingItems, mutedSmall, paths, Stack, useEnumLabel, VERIFICATION } from './shared';

const FROZEN = new Set(['COMPLETE', 'SUPERSEDED', 'EXPIRED', 'INVALID']);

type Doc = DossierDetail['documents'][number];
type History = DossierDetail['status_history'][number];
type Version = DossierDetail['versions'][number];

/** `/dossiers/:id`: one dossier version. Registration-form values are never shown, only field names. */
export function DossierPage() {
  const { t } = useTranslation('accounts');
  const { t: tq } = useTranslation('dossiers');
  const access = useAccess();
  const label = useEnumLabel('accounts');
  const L = useLabels();
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getDossier(id, { signal }), [id]);
  const title = result.status === 'success' ? t('dossier.title', { domain: result.data.unicode_name }) : t('dossier.fallbackTitle');
  const formFieldLabel = (k: string) => (t as unknown as (key: string, o: { defaultValue: string }) => string)(`dossier.formField.${k}`, { defaultValue: humanize(k) });
  useDocumentTitle(title);

  const docColumns: Column<Doc>[] = [
    { key: 'purpose', header: t('dossier.docCol.purpose'), cell: (d) => <EnumText group="purpose" value={d.purpose} /> },
    {
      key: 'document',
      header: t('dossier.docCol.document'),
      cell: (d) => <EntityRef to={paths.document(d.document_id)} id={d.document_id} label={label('documentType', d.document_type)} className="min-w-36" />,
    },
    { key: 'class', header: t('dossier.docCol.classification'), cell: (d) => <EnumBadge group="classification" tones={CLASSIFICATION} value={d.classification} /> },
    {
      key: 'version',
      header: t('dossier.docCol.version'),
      cell: (d) => (
        <span className="text-xs">
          {t('common.versionN', { n: d.version_number })}
          <span className={`block ${mutedSmall}`}>
            {d.mime_type} · {formatBytes(d.size_bytes)}
          </span>
        </span>
      ),
    },
    {
      key: 'verification',
      header: t('dossier.docCol.verification'),
      cell: (d) => (d.verification_status ? <EnumBadge group="docVerification" tones={DOC_VERIFICATION} value={d.verification_status} /> : <span className="text-muted-foreground">{t('common.none')}</span>),
    },
    { key: 'expires', header: t('dossier.docCol.expires'), className: 'whitespace-nowrap', cell: (d) => formatDate(d.expires_at) },
    { key: 'bound', header: t('dossier.docCol.bound'), className: 'whitespace-nowrap', cell: (d) => <span title={formatDateTime(d.bound_at)}>{formatDate(d.bound_at)}</span> },
  ];
  const historyColumns: Column<History & { i: number }>[] = [
    { key: 'when', header: t('dossier.historyCol.when'), className: 'whitespace-nowrap', cell: (h) => formatDateTime(h.occurred_at) },
    { key: 'from', header: t('dossier.historyCol.from'), cell: (h) => (h.from_status ? <EnumBadge group="dossierStatus" tones={DOSSIER_STATUS} value={h.from_status} /> : '—') },
    { key: 'to', header: t('dossier.historyCol.to'), cell: (h) => <EnumBadge group="dossierStatus" tones={DOSSIER_STATUS} value={h.to_status} /> },
    {
      key: 'missing',
      header: t('dossier.historyCol.missing'),
      className: 'whitespace-normal',
      cell: (h) => (h.missing_items.length ? <MissingItems items={h.missing_items} /> : '—'),
    },
    { key: 'reason', header: t('dossier.historyCol.reason'), className: 'whitespace-normal', cell: (h) => (h.reason ? <SystemReason reason={h.reason} /> : '—') },
    {
      key: 'actor',
      header: t('dossier.historyCol.actor'),
      cell: (h) =>
        h.actor_name ? (
          <span className="space-y-0.5">
            <span className="block font-medium">{h.actor_name}</span>
            <span className={`block ${mutedSmall}`}>{label('actorType', h.actor_type)}</span>
          </span>
        ) : (
          <EnumText group="actorType" value={h.actor_type} />
        ),
    },
  ];

  let body;
  if (result.status === 'loading') body = <Loading label={t('dossier.loading')} rows={4} />;
  else if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    body = <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('dossier.notFound') : t('dossier.loadError')} />;
  } else {
    const x = result.data;
    const frozen = FROZEN.has(x.status) || x.completed_at !== null;
    const versionColumns: Column<Version>[] = [
      {
        key: 'version',
        header: t('dossier.versionCol.version'),
        cell: (v) =>
          v.id === x.id ? (
            <strong>{t('dossier.thisVersion', { n: v.dossier_version })}</strong>
          ) : (
            <Link to={paths.dossier(v.id)} className={cellLink}>
              {t('common.versionN', { n: v.dossier_version })}
            </Link>
          ),
      },
      { key: 'status', header: t('dossier.versionCol.status'), cell: (v) => <EnumBadge group="dossierStatus" tones={DOSSIER_STATUS} value={v.status} /> },
      { key: 'created', header: t('dossier.versionCol.created'), className: 'whitespace-nowrap', cell: (v) => formatDateTime(v.created_at) },
      { key: 'completed', header: t('dossier.versionCol.completed'), className: 'whitespace-nowrap', cell: (v) => formatDateTime(v.completed_at) },
    ];
    body = (
      <Stack>
        {x.superseded_by_dossier_id ? (
          <Callout tone="warn" title={t('dossier.supersededTitle')}>
            <Trans
              t={t}
              i18nKey="dossier.superseded"
              values={{ n: x.superseded_by_dossier_version ?? '?' }}
              components={{ link: <Link to={paths.dossier(x.superseded_by_dossier_id)} className={cellLink} /> }}
            />
          </Callout>
        ) : null}
        <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
          <Section title={t('dossier.section')}>
            <KeyValue
              columns={2}
              rows={[
                [t('common.status'), <EnumBadge key="s" group="dossierStatus" tones={DOSSIER_STATUS} value={x.status} />],
                [t('dossier.field.domain'), <EntityRef key="d" to={paths.domain(x.ascii_name)} id={x.domain_id} label={x.unicode_name} sub={x.unicode_name !== x.ascii_name ? x.ascii_name : undefined} />],
                [t('dossier.field.lifecycle'), <StatusBadge key="l" map={LIFECYCLE} value={x.domain_lifecycle_state} />],
                [t('dossier.field.customer'), <EntityRef key="c" to={paths.customer(x.customer_id)} id={x.customer_id} label={x.customer_name ?? undefined} />],
                [t('common.version'), `${t('common.versionN', { n: x.dossier_version })}${frozen ? ` (${t('dossier.frozen')})` : ''}`],
                [t('common.type'), <EnumText key="t" group="dossierType" value={x.dossier_type} />],
                [t('dossier.field.policy'), L.policy(x.policy_key, x.policy_version)],
                [
                  t('dossier.field.supersedes'),
                  x.supersedes_dossier_id ? (
                    <Link key="sup" to={paths.dossier(x.supersedes_dossier_id)} className={cellLink}>
                      {x.supersedes_dossier_version ? t('common.versionN', { n: x.supersedes_dossier_version }) : t('dossier.fallbackTitle')}
                    </Link>
                  ) : (
                    '—'
                  ),
                ],
                [t('dossier.field.verified'), formatDateTime(x.verified_at)],
                [t('dossier.field.completed'), formatDateTime(x.completed_at)],
                [t('common.updated'), formatDateTime(x.updated_at)],
                [t('common.id'), <CopyText key="id" value={x.id} />],
              ]}
            />
          </Section>

          <Section title={t('dossier.registrant')}>
            {x.subject_id ? (
              <KeyValue
                columns={2}
                rows={[
                  [t('dossier.registrantField.subject'), <EntityRef key="s" to={paths.subject(x.subject_id)} id={x.subject_id} label={x.subject_legal_name ?? t('common.hiddenName')} />],
                  [t('common.type'), x.subject_type ? <EnumText key="st" group="subjectType" value={x.subject_type} /> : '—'],
                  [t('dossier.registrantField.pinnedVersion'), x.subject_version_number ? <span key="v" title={x.subject_version_id ?? undefined}>{t('common.versionN', { n: x.subject_version_number })}</span> : '—'],
                  [t('dossier.registrantField.verification'), x.verification_status ? <EnumBadge key="vs" group="verification" tones={VERIFICATION} value={x.verification_status} /> : t('common.none')],
                  [t('dossier.registrantField.method'), x.verification_method ? <EnumText key="vm" group="verificationMethod" value={x.verification_method} /> : '—'],
                  [
                  t('dossier.registrantField.provider'),
                  x.verification_provider
                    ? (t as unknown as (k: string, o: { defaultValue: string }) => string)(`dossier.verificationProvider.${x.verification_provider.toUpperCase()}`, { defaultValue: x.verification_provider })
                    : '—',
                ],
                  [t('dossier.registrantField.reviewer'), x.verification_reviewer_name ?? '—'],
                  [t('dossier.registrantField.expires'), formatDate(x.verification_expires_at)],
                ]}
              />
            ) : (
              <p className="text-sm text-muted-foreground">{t('dossier.noSubject')}</p>
            )}
          </Section>
        </div>

        <Tabs defaultValue="documents">
          <TabsList className="max-w-full flex-wrap justify-start group-data-[orientation=horizontal]/tabs:h-auto">
            <TabsTrigger value="documents">{t('dossier.tab.documents', { count: x.documents.length })}</TabsTrigger>
            <TabsTrigger value="form">{t('dossier.tab.form')}</TabsTrigger>
            <TabsTrigger value="history">{t('dossier.tab.history')}</TabsTrigger>
            {x.versions.length > 1 ? <TabsTrigger value="versions">{t('dossier.tab.versions', { count: x.versions.length })}</TabsTrigger> : null}
          </TabsList>
          <TabsContent value="documents">
            <Section flush>
              <DataTable columns={docColumns} rows={x.documents} rowKey={(d) => d.binding_id} empty={<EmptyState title={t('dossier.noDocuments')} />} />
            </Section>
          </TabsContent>
          <TabsContent value="form">
            <Section>
              <div className="space-y-4">
                <KeyValue
                  rows={[
                    [t('dossier.formFields'), x.registration_form_fields.length ? <span key="ff">{x.registration_form_fields.map(formFieldLabel).join(', ')}</span> : t('common.none')],
                    [
                      t('dossier.consents'),
                      x.consents.length ? (
                        <ul key="cs" className="space-y-1">
                          {x.consents.map((c, i) => (
                            <li key={i}>
                              {c.type ? <EnumText group="consentType" value={c.type} /> : t('dossier.consentFallback')}{' '}
                              <span className={mutedSmall}>
                                {t('dossier.consentMeta', { version: c.text_version ?? '?', actor: c.actor_type ? label('actorType', c.actor_type) : '—', at: formatDateTime(c.accepted_at) })}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        t('dossier.noConsents')
                      ),
                    ],
                  ]}
                />
                <Callout tone="info">{t('dossier.formNote')}</Callout>
              </div>
            </Section>
          </TabsContent>
          <TabsContent value="history">
            <Section flush>
              <DataTable
                columns={historyColumns}
                rows={x.status_history.map((h, i) => ({ ...h, i }))}
                rowKey={(h) => String(h.i)}
                empty={<EmptyState title={t('dossier.noHistory')} />}
              />
            </Section>
          </TabsContent>
          {x.versions.length > 1 ? (
            <TabsContent value="versions">
              <Section flush>
                <DataTable columns={versionColumns} rows={x.versions} rowKey={(v) => v.id} />
              </Section>
            </TabsContent>
          ) : null}
        </Tabs>
      </Stack>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        actions={result.status === 'success' && !FROZEN.has(result.data.status) && !result.data.completed_at && !access.client && access.has(['dossier.review', 'compliance.read', 'work_item.read']) ? (
          <Button size="sm" asChild><Link to={`/?tab=dossiers&open=${encodeURIComponent(id)}`}><FolderOpen /> {tq('title')}</Link></Button>
        ) : undefined}
        crumbs={[{ label: t('crumb') }, { label: t('dossiers.title'), to: '/dossiers' }, { label: result.status === 'success' ? result.data.unicode_name : t('dossier.fallbackTitle') }]}
      />
      {body}
    </>
  );
}
